import React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { FiDroplet, FiZap, FiActivity } from "react-icons/fi";
import { EnergyDistribution, EnergyType } from "../../api/dashboard";

type Props = {
  label: string;
  data: EnergyDistribution[];
  labels?: Partial<Record<EnergyType, string>>;
};

const ORDER: EnergyType[] = ["thermal", "hybrid", "electric"];

const ICONS: Record<EnergyType, React.ReactNode> = {
  thermal: <FiDroplet size={14} />,
  hybrid: <FiActivity size={14} />,
  electric: <FiZap size={14} />,
};

/**
 * Compact KPI card showing the fleet's energy mix (thermal / hybrid /
 * electric) as a stacked bar with per-segment percentages. Sits alongside
 * the other StatCards in the top row.
 */
export const EnergyMixCard: React.FC<Props> = ({ label, data, labels }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  const colors: Record<EnergyType, string> = {
    thermal: theme.palette.warning.main,
    hybrid: theme.palette.secondary.main,
    electric: theme.palette.success.main,
  };

  const byType = Object.fromEntries(data.map((d) => [d.type, d])) as Record<EnergyType, EnergyDistribution | undefined>;

  return (
    <Card elevation={0} sx={{ p: 2.25, height: "100%" }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mb: 1.5 }}>
        {label}
      </Typography>

      <Stack
        direction="row"
        sx={{
          height: 9,
          borderRadius: 999,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.text.primary, 0.06),
          mb: 1.5,
        }}
      >
        {ORDER.map((type) => {
          const pct = byType[type]?.percentage ?? 0;
          if (pct <= 0) return null;
          return (
            <Box key={type} sx={{ width: `${pct}%`, bgcolor: colors[type], transition: "width 400ms ease" }} />
          );
        })}
      </Stack>

      <Stack spacing={0.75}>
        {ORDER.map((type) => {
          const entry = byType[type];
          const pct = entry?.percentage ?? 0;
          return (
            <Stack key={type} direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(colors[type], isLight ? 0.14 : 0.2),
                    color: colors[type],
                  }}
                >
                  {ICONS[type]}
                </Box>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  {labels?.[type] ?? type}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>{pct}%</Typography>
            </Stack>
          );
        })}
      </Stack>
    </Card>
  );
};
